import { AlertTriangle, ChevronDown, ChevronRight, Copy, Film, Loader2, X } from "lucide-react"
import { useMemo, useState, type ReactNode } from "react"
import { toast } from "sonner"
import { GenerationLogJsonView, GenerationLogResponseView } from "@/components/generation-log-json-view"
import { GenerationPhaseLine } from "@/components/jobs/generation-phase-line"
import { GenerationStepTrack } from "@/components/jobs/generation-step-track"
import { formatElapsed, JobElapsedTimer, jobStatusLabel } from "@/components/jobs/job-elapsed-timer"
import { JobSubjectLabel } from "@/components/jobs/job-subject-label"
import { Button } from "@/components/ui/primitives"
import { useTranslation } from "@/hooks/use-translation"
import i18n from "@/i18n"
import {
  assetsForExchange,
  compactTimeline,
  extractRequestPrompt,
  extractRequestSettings,
  formatClockTime,
  pollGroups,
  requestAttempts,
  type GenerationDetailLog,
  type GenerationJobDetailData,
  type GenerationJobEvent,
  type GenerationTimelineRow,
  type RequestLogAsset,
} from "@/lib/generation-job-detail"
import { formatPhaseSpan, type GenerationJobTelemetry, type GenerationPhaseTranslate } from "@/lib/generation-phase"
import type { JobSubjectLabelSource } from "@/lib/job-subject-label"
import { generationLogStatusLabel } from "@/lib/shot-i18n"
import { cn } from "@/lib/utils"

const translatePhase: GenerationPhaseTranslate = (key, options) => i18n.t(`jobs:phase.${key}` as never, options)

const SECTION_TITLE = "text-[11px] font-semibold tracking-[0.12em] text-ui-text-muted uppercase"

export type GenerationDetailJob = JobSubjectLabelSource & {
  id: number
  status: string
  started_at?: string | null
  created_at?: string | null
  updated_at?: string | null
  error_message?: string | null
  telemetry?: GenerationJobTelemetry | null
}

export const GENERATION_STATUS_TEXT: Record<string, string> = {
  queued: "text-ui-text-secondary",
  processing: "text-violet-300",
  completed: "text-ui-status-approved",
  failed: "text-ui-status-failed",
  cancelled: "text-ui-text-muted",
}

function phaseName(phase: string): string {
  const key = `jobs:phase.phaseName.${phase}`
  return i18n.exists(key) ? i18n.t(key as never) : phase
}

function isActive(status: string) {
  return status === "queued" || status === "processing"
}

function StalledNote({ telemetry }: { telemetry: GenerationJobTelemetry }) {
  const { t } = useTranslation("jobs")
  const meta = telemetry.phase_meta ?? {}
  const reason = typeof meta.reason === "string" ? meta.reason : "no_heartbeat"
  const from = typeof meta.stalled_from === "string" ? phaseName(meta.stalled_from) : "—"
  const text =
    reason === "preparing_refs"
      ? t("detail.stalledPreparingRefs")
      : reason === "park_overdue"
        ? t("detail.stalledParkOverdue")
        : t("detail.stalledNoHeartbeat", { phase: from })
  return (
    <p className="flex max-w-[500px] items-start gap-1.5 text-xs leading-relaxed text-ui-text-secondary">
      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ui-status-rendering" />
      {text}
    </p>
  )
}

function eventNote(
  event: GenerationTimelineRow,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  const meta = event.meta ?? {}
  const parts: string[] = []
  const worker = meta.worker
  if (worker && typeof worker === "object" && !Array.isArray(worker)) {
    const w = worker as Record<string, unknown>
    if (w.host || w.pid) parts.push(t("detail.worker", { host: String(w.host ?? "?"), pid: String(w.pid ?? "?") }))
  }
  if (typeof meta.provider_status === "string" && meta.provider_status) parts.push(meta.provider_status)
  if (typeof meta.refs === "number" && meta.refs > 0) parts.push(t("detail.refsNote", { count: meta.refs }))
  if (event.attempts > 1) parts.push(t("detail.attemptsNote", { count: event.attempts }))
  if (event.polls > 0) parts.push(t("detail.pollsNote", { count: event.polls }))
  if (event.resumes > 0) parts.push(t("detail.resumesNote", { count: event.resumes }))
  if (typeof meta.provider_task_id === "string" && meta.provider_task_id) parts.push(meta.provider_task_id)
  return parts.join(" · ")
}

function Timeline({
  events,
  synthetic,
  active,
}: {
  events: GenerationJobEvent[]
  synthetic: boolean
  active: boolean
}) {
  const { t, locale } = useTranslation("jobs")
  const tr = t as unknown as (key: string, options?: Record<string, unknown>) => string
  const timeLocale = locale === "en" ? "en-US" : "ru-RU"
  const rows = useMemo(() => compactTimeline(events), [events])

  return (
    <section className="flex flex-col gap-0.5">
      <h3 className={cn(SECTION_TITLE, "pb-2")}>{t("detail.timeline")}</h3>
      {rows.length === 0 ? <p className="text-xs text-ui-text-muted">{t("detail.timelineEmpty")}</p> : null}
      {rows.map((event, i) => {
        const current = active && i === rows.length - 1 && event.duration_ms == null
        const warn = event.phase === "stalled"
        const note = eventNote(event, tr)
        return (
          <div key={`${event.phase}-${event.started_at}`} className="flex gap-3 py-1">
            <span className="w-16 shrink-0 text-[11px] text-ui-text-muted tabular-nums">
              {formatClockTime(event.started_at, timeLocale)}
            </span>
            <span
              className={cn(
                "mt-1.5 h-[7px] w-[7px] shrink-0 rounded-full",
                warn ? "bg-amber-400" : current ? "bg-violet-300" : "bg-ui-accent",
              )}
            />
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span
                className={cn(
                  "text-xs",
                  warn ? "text-amber-400" : current ? "font-medium text-ui-text-primary" : "text-ui-text-secondary",
                )}
              >
                {phaseName(event.phase)}
              </span>
              {note ? <span className="text-[11px] break-all text-ui-text-muted">{note}</span> : null}
            </div>
            <span className="text-[11px] text-ui-text-muted tabular-nums">
              {current
                ? t("detail.timelineNow")
                : event.duration_ms != null
                  ? formatPhaseSpan(event.duration_ms, translatePhase)
                  : ""}
            </span>
          </div>
        )
      })}
      {synthetic ? <p className="pt-1 text-[11px] text-ui-text-muted">{t("detail.timelineSynthetic")}</p> : null}
    </section>
  )
}

function AssetTile({ asset }: { asset: RequestLogAsset }) {
  const { t } = useTranslation("jobs")
  const isImage = asset.content_type?.startsWith("image/") && asset.url
  const caption = asset.owner_label || asset.filename || asset.external_ref || asset.json_path
  return (
    <div className="flex w-[132px] flex-col gap-1">
      {isImage ? (
        <a href={asset.url ?? undefined} target="_blank" rel="noreferrer" className="block">
          <img
            src={asset.url ?? undefined}
            alt={caption}
            loading="lazy"
            className="h-[74px] w-full rounded-md bg-ui-surface-sunken object-cover"
          />
        </a>
      ) : (
        <a
          href={asset.url ?? undefined}
          target="_blank"
          rel="noreferrer"
          className="flex h-[74px] w-full items-center justify-center rounded-md bg-ui-surface-sunken text-ui-text-muted"
        >
          <Film className="h-4 w-4" />
        </a>
      )}
      <span className="truncate text-[11px] text-ui-text-secondary" title={caption}>
        {caption}
      </span>
      <span className="truncate text-[10px] text-ui-text-muted">
        {[asset.role, t(`detail.delivery.${asset.delivery}`)].filter(Boolean).join(" · ")}
      </span>
    </div>
  )
}

function RequestTab({ log }: { log: GenerationDetailLog }) {
  const { t } = useTranslation("jobs")
  const attempts = requestAttempts(log.response_payload, log.request_payload)

  if (log.status === "pending" && attempts.length === 0) {
    return <p className="text-xs text-ui-text-muted">{t("detail.pendingRequest")}</p>
  }
  if (attempts.length === 0) return <p className="text-xs text-ui-text-muted">{t("detail.noBody")}</p>

  return (
    <div className="flex flex-col gap-5">
      {attempts.map((attempt, n) => {
        const prompt = extractRequestPrompt(attempt.request)
        const settings = extractRequestSettings(attempt.request)
        const assets = assetsForExchange(log.assets, attempt.index)
        return (
          <div key={attempt.index} className="flex flex-col gap-3">
            {attempts.length > 1 ? (
              <p className="text-[11px] text-ui-text-muted">
                {t("detail.attempt", { n: n + 1 })}
                {attempt.url ? <span className="ml-2 font-mono">{attempt.url}</span> : null}
              </p>
            ) : null}
            {prompt ? (
              <div className="flex flex-col gap-1">
                <span className={SECTION_TITLE}>{t("detail.prompt")}</span>
                <p className="text-xs leading-relaxed whitespace-pre-wrap text-ui-text-primary">{prompt}</p>
              </div>
            ) : null}
            {assets.length > 0 ? (
              <div className="flex flex-col gap-1.5">
                <span className={SECTION_TITLE}>{t("detail.assets")}</span>
                <div className="flex flex-wrap gap-3">
                  {assets.map((asset) => (
                    <AssetTile key={`${asset.exchange_index}-${asset.json_path}`} asset={asset} />
                  ))}
                </div>
              </div>
            ) : null}
            {settings.length > 0 ? (
              <div className="flex flex-col gap-1">
                <span className={SECTION_TITLE}>{t("detail.settings")}</span>
                <dl className="grid grid-cols-[minmax(0,180px)_minmax(0,1fr)] gap-x-3 gap-y-0.5 text-xs">
                  {settings.map(([key, value]) => (
                    <div key={key} className="contents">
                      <dt className="truncate font-mono text-ui-text-muted">{key}</dt>
                      <dd className="m-0 truncate text-ui-text-secondary">{value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            ) : null}
            <details className="group">
              <summary className="cursor-pointer text-[11px] text-ui-text-muted hover:text-ui-text-secondary">
                {t("detail.rawBody")}
              </summary>
              <GenerationLogJsonView value={attempt.request} className="mt-1.5" />
            </details>
          </div>
        )
      })}
    </div>
  )
}

function PollsTab({ log }: { log: GenerationDetailLog }) {
  const { t, locale } = useTranslation("jobs")
  const groups = pollGroups(log.response_payload)
  if (groups.length === 0) return <p className="text-xs text-ui-text-muted">{t("detail.noPolls")}</p>
  const timeLocale = locale === "en" ? "en-US" : "ru-RU"
  return (
    <div className="flex flex-col gap-0.5">
      {groups.map((group) => (
        <div key={group.index} className="flex items-baseline gap-3 py-1 text-xs">
          <span className="w-24 truncate font-mono text-ui-text-primary">{group.status}</span>
          <span className="w-12 text-ui-text-secondary">×{group.count}</span>
          <span className="text-ui-text-muted">
            {group.lastPolledAt
              ? t("detail.pollLast", { time: formatClockTime(group.lastPolledAt, timeLocale) })
              : null}
          </span>
        </div>
      ))}
    </div>
  )
}

type LogTab = "request" | "response" | "polls"

function RequestLogBlock({
  log,
  defaultOpen,
  extras,
}: {
  log: GenerationDetailLog
  defaultOpen: boolean
  extras?: ReactNode
}) {
  const { t, locale } = useTranslation("jobs")
  const [open, setOpen] = useState(defaultOpen)
  const [tab, setTab] = useState<LogTab>("request")
  const status = generationLogStatusLabel(log.status)
  const polls = pollGroups(log.response_payload).reduce((sum, group) => sum + group.count, 0)
  const timeLocale = locale === "en" ? "en-US" : "ru-RU"
  const tabs: [LogTab, string][] = [
    ["request", t("detail.tabRequest")],
    ["response", t("detail.tabResponse")],
    ["polls", t("detail.tabPolls", { count: polls })],
  ]

  return (
    <div className="flex flex-col rounded-lg bg-ui-surface-sunken">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex items-center gap-2.5 px-3 py-2.5 text-left"
      >
        <span className={cn("w-20 shrink-0 text-[11px]", status.cls)}>{status.label}</span>
        <span className="min-w-0 flex-1 truncate font-mono text-xs text-ui-text-primary">
          {log.api_endpoint || log.provider}
        </span>
        <span className="shrink-0 text-[11px] text-ui-text-muted tabular-nums">
          {formatClockTime(log.created_at, timeLocale).slice(0, 5)}
          {log.duration_ms != null ? ` · ${(log.duration_ms / 1000).toFixed(1)} s` : ""}
        </span>
        {open ? (
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-ui-text-muted" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 shrink-0 text-ui-text-muted" />
        )}
      </button>
      {open ? (
        <div className="flex flex-col gap-3 px-3 pb-3">
          <p className="text-[11px] text-ui-text-muted">
            {[log.provider, log.ai_model, log.external_task_id].filter(Boolean).join(" · ")}
          </p>
          {log.error_message ? <p className="text-xs text-ui-status-failed">{log.error_message}</p> : null}
          {extras}
          <div role="tablist" className="flex gap-0.5">
            {tabs.map(([key, label]) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={tab === key}
                onClick={() => setTab(key)}
                className={cn(
                  "rounded-md px-2.5 py-1 text-xs",
                  tab === key
                    ? "bg-ui-surface-raised text-ui-text-primary"
                    : "text-ui-text-muted hover:text-ui-text-secondary",
                )}
              >
                {label}
              </button>
            ))}
          </div>
          {tab === "request" ? <RequestTab log={log} /> : null}
          {tab === "response" ? (
            log.response_payload ? (
              <GenerationLogResponseView value={log.response_payload} />
            ) : (
              <p className="text-xs text-ui-text-muted">{t("detail.noResponse")}</p>
            )
          ) : null}
          {tab === "polls" ? <PollsTab log={log} /> : null}
        </div>
      ) : null}
    </div>
  )
}

export type GenerationJobDetailProps = {
  job: GenerationDetailJob
  projectId: number
  /** Строка под заголовком: вид работы, модель, автор, время запуска. */
  meta?: ReactNode
  data: GenerationJobDetailData | null
  loading?: boolean
  error?: string | null
  /** Журнал запросов виден поддержке проекта и платформе. */
  canViewLogs?: boolean
  onClose?: () => void
  onStop?: () => void
  stopping?: boolean
  /** Действия над строкой журнала (дополучить по task_id и т.п.) — у вызывающей стороны. */
  logExtras?: (log: GenerationDetailLog) => ReactNode
  /** Без заголовка — когда его уже рисует модалка. */
  hideHeader?: boolean
  className?: string
}

/** Панель генерации: фаза, ход, запросы к провайдеру с телом, ассетами и опросами. */
export function GenerationJobDetail({
  job,
  projectId,
  meta,
  data,
  loading = false,
  error = null,
  canViewLogs = true,
  onClose,
  onStop,
  stopping = false,
  logExtras,
  hideHeader = false,
  className,
}: GenerationJobDetailProps) {
  const { t } = useTranslation(["jobs", "monitoring"])
  const active = isActive(job.status)
  const telemetry = active ? (data?.telemetry ?? job.telemetry ?? null) : null
  const logs = data?.logs ?? []
  const events = data?.events ?? []
  const taskId = telemetry?.provider_task_id ?? logs.find((log) => log.external_task_id)?.external_task_id ?? null
  const finishedMs =
    !active && job.started_at && job.updated_at
      ? new Date(job.updated_at).getTime() - new Date(job.started_at).getTime()
      : null

  const copyTaskId = () => {
    if (!taskId) return
    void navigator.clipboard?.writeText(taskId).then(() => toast.success(t("jobs:detail.copied")))
  }

  return (
    <div className={cn("flex flex-col gap-6", className)}>
      {hideHeader ? null : (
        <div className="flex items-start gap-3">
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <div className="text-base font-semibold">
              <JobSubjectLabel job={job} projectId={projectId} tone="plain" />
            </div>
            {meta ? <div className="text-xs text-ui-text-muted">{meta}</div> : null}
          </div>
          {onClose ? (
            <button
              type="button"
              aria-label={t("jobs:detail.close")}
              onClick={onClose}
              className="rounded-md p-1 text-ui-text-muted hover:bg-ui-surface-raised hover:text-ui-text-primary"
            >
              <X className="h-4 w-4" />
            </button>
          ) : null}
        </div>
      )}

      <div className="flex flex-col gap-2.5">
        <div className="flex items-baseline gap-2.5 text-xs">
          <span className={GENERATION_STATUS_TEXT[job.status] ?? "text-ui-text-secondary"}>
            {telemetry?.phase === "stalled" ? t("jobs:detail.stalledTitle") : jobStatusLabel(job.status)}
          </span>
          {active && job.started_at ? (
            <JobElapsedTimer startedAt={job.started_at} active />
          ) : finishedMs != null && finishedMs > 0 ? (
            <span className="text-ui-text-muted tabular-nums">{formatElapsed(finishedMs)}</span>
          ) : null}
        </div>
        {telemetry?.phase ? (
          <GenerationPhaseLine
            telemetry={telemetry}
            className={cn("text-[15px]", telemetry.phase === "stalled" ? "text-amber-400" : "text-ui-text-primary")}
          />
        ) : null}
        {telemetry?.phase === "stalled" ? <StalledNote telemetry={telemetry} /> : null}
        {!active && job.error_message ? (
          <p className="text-xs leading-relaxed text-ui-status-failed">{job.error_message}</p>
        ) : null}
        <GenerationStepTrack telemetry={telemetry} labels />
        {(active && onStop) || taskId ? (
          <div className="flex flex-wrap gap-2 pt-1">
            {active && onStop ? (
              <Button type="button" variant="secondary" size="sm" onClick={onStop} disabled={stopping}>
                {stopping ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}
                {t("monitoring:generations.stop")}
              </Button>
            ) : null}
            {taskId ? (
              <button
                type="button"
                onClick={copyTaskId}
                title={t("jobs:detail.copyTaskId")}
                className="flex items-center gap-1.5 rounded-md px-2 font-mono text-xs text-ui-text-muted hover:text-ui-text-secondary"
              >
                {taskId}
                <Copy className="h-3 w-3" />
              </button>
            ) : null}
          </div>
        ) : null}
      </div>

      {loading && !data ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-ui-link" />
        </div>
      ) : error ? (
        <p className="text-xs text-ui-status-failed">{error}</p>
      ) : (
        <>
          <Timeline events={events} synthetic={data?.events_synthetic === true} active={active} />
          <section className="flex flex-col gap-1.5">
            <h3 className={cn(SECTION_TITLE, "pb-1")}>{t("jobs:detail.requests", { count: logs.length })}</h3>
            {!canViewLogs ? (
              <p className="text-xs text-ui-text-muted">{t("jobs:detail.requestsHidden")}</p>
            ) : logs.length === 0 ? (
              <p className="text-xs text-ui-text-muted">{t("jobs:detail.noRequests")}</p>
            ) : (
              logs.map((log, i) => (
                <RequestLogBlock
                  key={log.id}
                  log={log}
                  defaultOpen={i === 0 && (logs.length === 1 || log.status === "sent" || log.status === "pending")}
                  extras={logExtras?.(log)}
                />
              ))
            )}
          </section>
        </>
      )}
    </div>
  )
}
